import {Injectable} from '@angular/core';
import {HttpService} from '@core/backend/common/api/http.service';
import {Observable} from 'rxjs';
import {
    APIResponse,
    DataTableColumn,
    Filter,
    FilterFieldOptionRequest,
    FilterFieldOptionResponse,
    GenericListRequest,
    PagingResponse,
    PresignedURL,
    Simple2DMultiSeries,
    SimpleMultiSeries,
    SimpleSeries,
    TableFilterConfig,
    YearQuarterPair,
} from '@core/interfaces/system/system-common';
import {DownloadCSVUrl} from '@core/interfaces/engin/workflow-validation';
import {DataSource} from '@mominsamir/ngx-smart-table/lib/lib/data-source/data-source';
import {
    ActivityBarChartDataDto,
    ChangeHistoryDto,
    InspectionChartComparisonRequest,
    InspectionResultCorrectionDto,
    InspectionResultDto,
    InspectionResultReviewDto,
    InspectionSummaryStatsDto,
} from '@core/interfaces/engin/maintenance-planning/maintenance-planning';
import {InspectionResultFullDto} from '@core/interfaces/engin/maintenance-planning/form-visualization';
import {AssetColumn} from '@core/interfaces/common/asset';
import {map} from 'rxjs/operators';
import {
    ActivityBulkUpdateDto,
    ActivityChangeStatus,
    ActivityCreateDto,
    ActivityDto,
    ActivityUpdateDto,
    CsvColumnRequest,
    DeficiencyBulkUpdateDto,
    DeficiencyChangeStatus,
    DeficiencyCreateDto,
    DeficiencyDto,
    DeficiencyFullDto,
    DeficiencyUpdateDto,
    InspectionStatsDto,
    LinkActivitiesToDeficiency,
} from '@core/interfaces/engin/maintenance-planning/deficiencies.model';
import {HttpParams} from '@angular/common/http';

@Injectable()
export class MaintenancePlanningApi {
    private readonly prefix = 'maintenance';
    private readonly prefixDeficiencies = 'maintenance/deficiencies';
    private readonly prefixActivities = 'maintenance/activities';

    constructor(private api: HttpService) {}

    // Inspection Details
    findOneDto(resultId: number): Observable<InspectionResultDto> {
        return this.api.get(`${this.prefix}/dto/${resultId}`);
    }

    findOneFullDto(resultId: number): Observable<InspectionResultFullDto> {
        return this.api.get(`${this.prefix}/dto-full/${resultId}`);
    }

    getChangeHistory(resultId: number): Observable<ChangeHistoryDto[]> {
        return this.api.get(`${this.prefix}/history/${resultId}`);
    }

    submitReview(resultId: number, req: InspectionResultReviewDto): Observable<InspectionResultDto> {
        return this.api.post(`${this.prefix}/review/${resultId}`, req);
    }

    submitCorrection(resultId: number, req: InspectionResultCorrectionDto): Observable<InspectionResultFullDto> {
        return this.api.post(`${this.prefix}/correction/${resultId}`, req);
    }

    findLatestInspectionResult(resultId: number): Observable<APIResponse<InspectionResultDto>> {
        return this.api.get(`${this.prefix}/latest/${resultId}`);
    }

    getPresignedUrl(fileName: string, fileFormat: string): Observable<APIResponse<PresignedURL>> {
        return this.api.post(`${this.prefix}/presigned-url`, {fileName, fileFormat});
    }

    upload(url: string, fileData: File): Observable<any> {
        return this.api.uploadS3(`${url}`, fileData, {
            reportProgress: true,
            observe: 'events',
            headers: {
                'Content-Disposition': 'attachment',
                'Content-Type': fileData.type,
            },
        });
    }

    // Options
    getFilterOptions(): Observable<APIResponse<TableFilterConfig[]>> {
        return this.api.get(`${this.prefix}/options/filters`);
    }

    getFilterFieldOptions(req: FilterFieldOptionRequest): Observable<APIResponse<FilterFieldOptionResponse>> {
        return this.api.post(`${this.prefix}/options/filters/field`, req);
    }

    // Inspection List
    listDto(): Observable<DataSource> {
        return this.api.getServerDataSource(`${this.prefix}/dto/list`);
    }

    listDtoCsv(paramsString?: string): Observable<APIResponse<DownloadCSVUrl>> {
        return this.api.get(`${this.prefix}/dto/list/csv${paramsString && '?' + paramsString}`);
    }

    // Inspection Dashboard
    getGroupOptions(): Observable<APIResponse<AssetColumn[]>> {
        return this.api.get(`${this.prefix}/options/groups`);
    }

    listInspectionAssetDto(req: GenericListRequest): Observable<DataSource> {
        return this.api.postServerDataSource(`${this.prefix}/dto-asset/list`, req);
    }

    getListInspectionAssetDtoCsv(paramsString: string, filterParams: Filter[]): Observable<DownloadCSVUrl> {
        return this.api
            .post(`${this.prefix}/dto-asset/list/csv${paramsString && '?' + paramsString}`, {filterParams})
            .pipe(map((res: APIResponse<DownloadCSVUrl>) => res.response));
    }

    getInspectionsByStatus(req: GenericListRequest): Observable<APIResponse<SimpleSeries<string, number>>> {
        return this.api.post(`${this.prefix}/chart/status`, req);
    }

    getInspectionsComparison(
        req: InspectionChartComparisonRequest,
    ): Observable<APIResponse<SimpleMultiSeries<string>>> {
        return this.api.post(`${this.prefix}/chart/comparison`, req);
    }

    // Asset Registry > Maintenance
    getInspectionsTimelineByParentAssetId(
        parentAssetId: string,
    ): Observable<APIResponse<SimpleSeries<YearQuarterPair, number>>> {
        return this.api.get(`${this.prefix}/chart/timeline/${parentAssetId}`);
    }

    getInspectionsSummaryByParentAssetId(parentAssetId: string): Observable<APIResponse<InspectionSummaryStatsDto>> {
        return this.api.get(`${this.prefix}/chart/summary/${parentAssetId}`);
    }

    listDtoByParentAssetId(parentAssetId: string): Observable<DataSource> {
        return this.api.getServerDataSource(`${this.prefix}/dto/list/${parentAssetId}`);
    }

    listDtoCsvByParentAssetId(parentAssetId: string, paramsString?: string): Observable<APIResponse<DownloadCSVUrl>> {
        return this.api.get(`${this.prefix}/dto/list/csv/${parentAssetId}${paramsString && '?' + paramsString}`);
    }

    // Deficiencies and Activities
    getDeficienciesDtoList(filterString: string = ''): Observable<DataSource> {
        return this.api.getServerDataSource(`${this.prefixDeficiencies}/dto/list?${filterString}`);
    }

    getDeficienciesListByParentId(parentAssetId: string, filterString: string = ''): Observable<DataSource> {
        return this.api.getServerDataSource(
            `${this.prefixDeficiencies}/dto/list/parentAssetId/${parentAssetId}?${filterString}`,
        );
    }

    getDeficienciesByInspectionId(inspectionResultCode: string): Observable<PagingResponse<DeficiencyDto>> {
        return this.api.get(
            `${this.prefixDeficiencies}/dto/list?filterByinspectionResultCode_EQUAL=${inspectionResultCode}&pageSize=200`,
        );
    }

    downloadDeficienciesList(req: CsvColumnRequest, paramsString?: string): Observable<DownloadCSVUrl> {
        return this.api.post(`${this.prefixDeficiencies}/dto/list/csv${paramsString && '?' + paramsString}`, req);
    }

    downloadDeficienciesListByParentId(
        parentAssetId: string,
        req: CsvColumnRequest,
        paramsString?: string,
    ): Observable<DownloadCSVUrl> {
        return this.api.post(
            `${this.prefixDeficiencies}/dto/list/csv/parentAssetId/${parentAssetId}${
                paramsString && '?' + paramsString
            }`,
            req,
        );
    }

    updateDeficiencyStatus(statusChangeRequest: DeficiencyChangeStatus): Observable<any> {
        return this.api.put(`${this.prefixDeficiencies}/update/status`, statusChangeRequest);
    }

    createDeficiency(deficiency: DeficiencyCreateDto): Observable<any> {
        return this.api.post(`${this.prefixDeficiencies}`, deficiency);
    }

    getDeficiencyFullDto(id: number): Observable<DeficiencyFullDto> {
        return this.api
            .get(`${this.prefixDeficiencies}/dto-full/${id}`)
            .pipe(map((res: APIResponse<DeficiencyFullDto>) => res.response));
    }

    updateDeficiency(id: number, deficiency: DeficiencyUpdateDto): Observable<any> {
        return this.api.put(`${this.prefixDeficiencies}/${id}`, deficiency);
    }

    getDeficienciesCategories(searchString: string): Observable<string[]> {
        return this.api
            .get(`${this.prefixDeficiencies}/list/categories`, {
                params: new HttpParams().set('category', searchString || ''),
            })
            .pipe(map((res: APIResponse<string[]>) => res.response));
    }

    getDeficienciesColumns(): Observable<DataTableColumn[]> {
        return this.api
            .get(`${this.prefixDeficiencies}/columns`)
            .pipe(map((res: APIResponse<DataTableColumn[]>) => res.response));
    }

    bulkUpdateDeficiency(req: DeficiencyBulkUpdateDto): Observable<any> {
        return this.api.put(`${this.prefixDeficiencies}/update/bulk`, req);
    }

    getDeficienciesFilterFieldOptions(
        req: FilterFieldOptionRequest,
    ): Observable<APIResponse<FilterFieldOptionResponse>> {
        return this.api.post(`${this.prefixDeficiencies}/options/filters/field`, req);
    }

    getDeficienciesFilterFieldOptionsByParentId(
        parentAssetId,
        req: FilterFieldOptionRequest,
    ): Observable<APIResponse<FilterFieldOptionResponse>> {
        return this.api.post(`${this.prefixDeficiencies}/options/filters/field/parentAssetId/${parentAssetId}`, req);
    }

    getActivitiesDtoList(filterString: string = ''): Observable<DataSource> {
        return this.api.getServerDataSource(`${this.prefixActivities}/dto/list?${filterString}`);
    }

    getActivitiesListByParentId(parentAssetId: string, filterString: string = '') {
        return this.api.getServerDataSource(
            `${this.prefixActivities}/dto/list/parentAssetId/${parentAssetId}?${filterString}`,
        );
    }

    getActivitiesList(filterString: string = ''): Observable<PagingResponse<ActivityDto>> {
        return this.api.get(`${this.prefixActivities}/dto/list?${filterString}`);
    }

    downloadActivitiesList(req: CsvColumnRequest, paramsString?: string): Observable<DownloadCSVUrl> {
        return this.api.post(`${this.prefixActivities}/dto/list/csv${paramsString && '?' + paramsString}`, req);
    }

    downloadActivitiesListByParentId(
        parentAssetId: string,
        req: CsvColumnRequest,
        paramsString?: string,
    ): Observable<DownloadCSVUrl> {
        return this.api.post(
            `${this.prefixActivities}/dto/list/csv/parentAssetId/${parentAssetId}${paramsString && '?' + paramsString}`,
            req,
        );
    }

    getActivityTimelineByAsset(assetId: string): Observable<APIResponse<Simple2DMultiSeries<YearQuarterPair, string>>> {
        return this.api.get(`${this.prefixActivities}/chart/timeline/${assetId}`);
    }

    getActivityBarChartByAsset(
        assetId: string,
    ): Observable<APIResponse<SimpleSeries<string, ActivityBarChartDataDto>>> {
        return this.api.get(`${this.prefixActivities}/chart/barChart/${assetId}`);
    }

    createActivity(activity: ActivityCreateDto): Observable<APIResponse<ActivityDto>> {
        return this.api.post(`${this.prefixActivities}`, activity);
    }

    updateActivityStatus(statusChangeRequest: ActivityChangeStatus): Observable<any> {
        return this.api.put(`${this.prefixActivities}/update/status`, statusChangeRequest);
    }

    getActivityFullDto(id: number): Observable<ActivityDto> {
        return this.api
            .get(`${this.prefixActivities}/dto-full/${id}`)
            .pipe(map((res: APIResponse<ActivityDto>) => res.response));
    }

    updateActivity(id: number, activity: ActivityUpdateDto): Observable<any> {
        return this.api.put(`${this.prefixActivities}/${id}`, activity);
    }

    getActivityCategories(searchString: string): Observable<string[]> {
        return this.api
            .get(`${this.prefixActivities}/list/categories`, {
                params: new HttpParams().set('category', searchString || ''),
            })
            .pipe(map((res: APIResponse<string[]>) => res.response));
    }

    getInspectionStats(id: number): Observable<InspectionStatsDto> {
        return this.api
            .get(`${this.prefixDeficiencies}/stats/inspection/${id}`)
            .pipe(map((res: APIResponse<InspectionStatsDto>) => res.response));
    }

    getActivitiesColumns(): Observable<DataTableColumn[]> {
        return this.api
            .get(`${this.prefixActivities}/columns`)
            .pipe(map((res: APIResponse<DataTableColumn[]>) => res.response));
    }

    bulkLinkDeficiency(req: LinkActivitiesToDeficiency): Observable<any> {
        return this.api.put(`${this.prefixActivities}/link/deficiency`, req);
    }

    getActivitiesFilterFieldOptions(req: FilterFieldOptionRequest): Observable<APIResponse<FilterFieldOptionResponse>> {
        return this.api.post(`${this.prefixActivities}/options/filters/field`, req);
    }

    getActivitiesFilterFieldOptionsByParentId(
        parentAssetId,
        req: FilterFieldOptionRequest,
    ): Observable<APIResponse<FilterFieldOptionResponse>> {
        return this.api.post(`${this.prefixActivities}/options/filters/field/parentAssetId/${parentAssetId}`, req);
    }

    bulkUpdateActivity(req: ActivityBulkUpdateDto): Observable<any> {
        return this.api.put(`${this.prefixActivities}/update/bulk`, req);
    }

    scanForDeficiencies(inspectionResultIdList: number[]): Observable<number> {
        return this.api
            .post(`${this.prefixDeficiencies}/scan`, {
                inspectionResultIdList,
            })
            .pipe(map((res: APIResponse<number>) => res.response));
    }

    getDeficienciesBarChartByAsset(assetId: string) {
        return this.api.get(`${this.prefixDeficiencies}/chart/barChart/${assetId}`);
    }

    getDeficienciesTimelineByAsset(assetId: string) {
        return this.api.get(`${this.prefixDeficiencies}/chart/timeline/${assetId}`, assetId);
    }
}
